import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Rocket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useSimulationForm } from '@/hooks/useSimulationForm';
import FileUploadField from '@/components/simulation/FileUploadField';
import DifficultySelector from '@/components/simulation/DifficultySelector';
import CustomSelect from '@/components/ui/CustomSelect';
import ConfirmDestructiveModal from '@/components/shared/ConfirmDestructiveModal';
import EmailVerificationBanner from '@/components/shared/EmailVerificationBanner.tsx';
import FlowSteps from '@/components/shared/FlowSteps.tsx';
import scoringService from '@/services/scoringService.ts';
import { criterionLabel, criterionDescription } from '@/types/scoring.ts';
import type { ScoringCriterion } from '@/types/scoring.ts';
import { maestroIdForDifficulty } from '@/types/maestro';

// Flujo B — "Crea tu mejor pitch" (fase 1 simplificada del MVP).
// Configurar (objetivo + qué evaluar) → simulación → informe orientado al pitch.
export default function PitchFlowPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [criteria, setCriteria] = useState<ScoringCriterion[]>([]);

  const {
    form,
    submitting,
    error,
    existingDocs,
    pendingFiles,
    uploadingFiles,
    dragOver,
    rejectedUploads,
    limitReached,
    hasRejectedUploads,
    atDocumentLimit,
    dismissRejectedUpload,
    clearLimitReached,
    showLeaveConfirm,
    salePhases,
    simulationTypeOptions,
    watchDifficulty,
    onSubmit,
    handleDrop,
    handleFileInput,
    removePendingFile,
    removeExistingDoc,
    setShowLeaveConfirm,
    setDragOver,
  } = useSimulationForm({
    onCreated: (sim) => navigate(`/simulation/${sim.id}`),
  });

  const { register, handleSubmit, watch, setValue, formState: { errors } } = form;

  // MVP pitch: purpose fijo + maestro (perfil) según la dificultad elegida.
  useEffect(() => {
    setValue('purpose', 'pitch');
    setValue('assignedMaestroId', maestroIdForDifficulty(watchDifficulty));
  }, [setValue, watchDifficulty]);

  // Criterios seleccionables (qué es importante evaluar).
  useEffect(() => {
    scoringService
      .getCriteria()
      .then((all) => setCriteria(all.filter((c) => c.enabled && c.appliesTo !== 'evaluate')))
      .catch(() => setCriteria([]));
  }, []);

  const selectedKpis = watch('kpisToEvaluate') ?? [];
  const toggleKpi = (key: string) => {
    setValue(
      'kpisToEvaluate',
      selectedKpis.includes(key)
        ? selectedKpis.filter((k) => k !== key)
        : [...selectedKpis, key],
      { shouldDirty: true },
    );
  };

  const handleBack = () => {
    if (form.formState.isDirty) {
      setShowLeaveConfirm(true);
    } else {
      navigate('/');
    }
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div className="mx-auto max-w-3xl animate-fade-in">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleBack}
          className="mb-6 gap-1.5 text-muted-foreground"
        >
          <ArrowLeft className="size-4" />
          {t('mvpFlow.backHome')}
        </Button>

        <FlowSteps current={1} />

        <div className="mb-4">
          <EmailVerificationBanner />
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-heading uppercase text-foreground">
            {t('mvpFlow.pitchTitle')}
          </h1>
          <p className="mt-2 text-muted-foreground">{t('mvpFlow.pitchSubtitle')}</p>
        </div>

        {error && (
          <div role="alert" className="mb-6 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Documentación + qué se vende */}
          <Field label={t('configurator.documentsLabel')} hint={t('mvpFlow.docsHelp')}>
            <FileUploadField
              existingDocs={existingDocs}
              pendingFiles={pendingFiles}
              uploadingFiles={uploadingFiles}
              dragOver={dragOver}
              rejectedUploads={rejectedUploads}
              atLimit={atDocumentLimit}
              limitReached={limitReached}
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onFileInput={handleFileInput}
              onRemovePending={removePendingFile}
              onRemoveExisting={removeExistingDoc}
              onDismissRejected={dismissRejectedUpload}
              onDismissLimitReached={clearLimitReached}
            />
          </Field>

          {/* Objetivo del pitch (meeting 03/06: base del scoring) */}
          <Field
            label={t('mvpFlow.pitchObjectiveLabel')}
            hint={t('mvpFlow.pitchObjectiveHelp')}
            error={errors.pitchObjective?.message}
          >
            <Textarea
              {...register('pitchObjective')}
              placeholder={t('mvpFlow.pitchObjectivePlaceholder')}
              rows={4}
              className="resize-none"
            />
          </Field>

          {/* Nombre */}
          <Field label={t('configurator.nameLabel')} error={errors.name?.message}>
            <Input {...register('name')} placeholder={t('mvpFlow.pitchNamePlaceholder')} />
          </Field>

          {/* Objetivo de la simulación (fase) + Tipo */}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('mvpFlow.simulationGoalLabel')} hint={t('mvpFlow.simulationGoalHelp')} error={errors.salePhase?.message}>
              <CustomSelect
                value={watch('salePhase') ?? ''}
                onChange={(val) => setValue('salePhase', val)}
                placeholder={t('configurator.selectPhase')}
                options={[
                  { value: '', label: t('configurator.selectPhase') },
                  ...salePhases.map((p) => ({ value: p.value, label: p.label })),
                ]}
              />
            </Field>
            <Field label={t('configurator.simulationTypeLabel')} error={errors.simulationType?.message}>
              <CustomSelect
                value={watch('simulationType') ?? ''}
                onChange={(val) => setValue('simulationType', val)}
                placeholder={t('configurator.selectType')}
                options={[
                  { value: '', label: t('configurator.selectType') },
                  ...simulationTypeOptions.map((o) => ({ value: o.value, label: o.label })),
                ]}
              />
            </Field>
          </div>

          {/* Qué es importante evaluar (criterios de scoring) */}
          {criteria.length > 0 && (
            <Field label={t('mvpFlow.kpisLabel')} hint={t('mvpFlow.kpisHelp')}>
              <div className="grid gap-2 sm:grid-cols-2">
                {criteria.map((c) => {
                  const checked = selectedKpis.includes(c.key);
                  return (
                    <label
                      key={c.key}
                      className={`flex cursor-pointer items-start gap-2.5 rounded-lg border p-3 text-sm transition-colors ${
                        checked ? 'border-primary/50 bg-primary/5' : 'border-border hover:border-primary/30'
                      }`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleKpi(c.key)}
                        className="mt-0.5"
                      />
                      <span>
                        <span className="font-medium text-foreground">
                          {criterionLabel(c, i18n.resolvedLanguage ?? 'es')}
                        </span>
                        <span className="mt-0.5 block text-xs text-muted-foreground">
                          {criterionDescription(c, i18n.resolvedLanguage ?? 'es')}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </Field>
          )}

          {/* Dificultad + Idioma */}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('configurator.difficultyLabel')}>
              <DifficultySelector
                value={watchDifficulty}
                onChange={(level) => setValue('difficultyLevel', level)}
              />
            </Field>
            <Field label={t('configurator.languageLabel')} error={errors.language?.message}>
              <CustomSelect
                value={watch('language') ?? 'es'}
                onChange={(val) => setValue('language', val as 'es' | 'it', { shouldDirty: true })}
                options={[
                  { value: 'es', label: t('configurator.languageEs') },
                  { value: 'it', label: t('configurator.languageIt') },
                ]}
              />
            </Field>
          </div>

          {/* Submit */}
          <div className="flex items-center justify-end pt-2">
            <Tooltip open={hasRejectedUploads && !submitting ? undefined : false}>
              <TooltipTrigger asChild>
                <span className="inline-block">
                  <Button type="submit" size="lg" disabled={submitting || hasRejectedUploads}>
                    {submitting ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Rocket className="size-4" />
                    )}
                    {t('mvpFlow.createAndStart')}
                  </Button>
                </span>
              </TooltipTrigger>
              {hasRejectedUploads && !submitting && (
                <TooltipContent>{t('configurator.submitBlockedRejected')}</TooltipContent>
              )}
            </Tooltip>
          </div>
        </form>
      </div>

      <ConfirmDestructiveModal
        isOpen={showLeaveConfirm}
        onClose={() => setShowLeaveConfirm(false)}
        onConfirm={() => navigate('/')}
        title={t('configurator.leaveTitle')}
        description={t('configurator.leaveMessage')}
        confirmLabel={t('configurator.leaveWithoutSaving')}
        cancelLabel={t('configurator.keepEditing')}
      />
    </TooltipProvider>
  );
}

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="relative rounded-xl border border-border bg-card p-4 sm:p-5 focus-within:z-10">
      <label className="mb-2 block text-sm font-semibold text-foreground">{label}</label>
      {hint && <p className="mb-3 text-xs text-muted-foreground">{hint}</p>}
      {children}
      {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
    </div>
  );
}
